import React from 'react';

import './PromptCard.css';

const formatRelativeDate = (isoString) => {
  if (!isoString) return 'Updated recently';

  const then = new Date(isoString).getTime();
  if (Number.isNaN(then)) return 'Updated recently';

  const diffMs = Date.now() - then;
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (diffMs < hour) {
    const minutes = Math.max(1, Math.round(diffMs / minute));
    return `Updated ${minutes}m ago`;
  }

  if (diffMs < day) {
    const hours = Math.max(1, Math.round(diffMs / hour));
    return `Updated ${hours}h ago`;
  }

  const days = Math.max(1, Math.round(diffMs / day));
  return `Updated ${days}d ago`;
};

const PromptCard = ({ item, title, tags, details, selected, onSelect, onLink }) => {
  const preview =
    item.input_summary?.goal ||
    item.input_summary?.targetOutcome ||
    item.metadata?.summary ||
    'Reusable prompt grounded in your course and project context.';

  const status = item.metadata?.status || item.metadata?.state || 'active';
  const cardVariantClass =
    status === 'draft'
      ? 'prompt-card--draft'
      : status === 'favourite'
        ? 'prompt-card--favourite'
        : 'prompt-card--active';

  const topMeta = [...details, { label: 'Type', value: item.type || '--' }].slice(0, 4);

  const handleOpen = (e) => {
    e.stopPropagation();
    onSelect(item.id);
  };

  const handleLink = (e) => {
    e.stopPropagation();
    onLink?.(item.id);
  };

  return (
    <div
      className={`prompt-card ${cardVariantClass} ${selected ? 'is-selected' : ''}`}
      onClick={() => onSelect(item.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(item.id);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="prompt-card__top">
        <div className="prompt-card__badge">Prompt</div>
        <button type="button" className="prompt-card__menu" onClick={(e) => e.stopPropagation()}>
          •••
        </button>
      </div>

      <div className="prompt-card__body">
        <h3 className="prompt-card__title">{title}</h3>
        <p className="prompt-card__preview">{preview}</p>

        <div className="prompt-card__meta">
          {topMeta.map((entry) => (
            <div key={`${item.id}-${entry.label}`} className="prompt-card__meta-item">
              <span className="prompt-card__meta-label">{entry.label}</span>
              <span className="prompt-card__meta-value">{entry.value}</span>
            </div>
          ))}
        </div>

        <div className="prompt-card__tags">
          {tags.length > 0 ? (
            tags.map((tag) => (
              <span key={`${item.id}-${tag}`} className="prompt-chip">
                {tag}
              </span>
            ))
          ) : (
            <span className="prompt-chip">No tags</span>
          )}
        </div>
      </div>

      <div className="prompt-card__footer">
        <span className="prompt-card__date">
          {formatRelativeDate(item.updated_at || item.created_at)}
        </span>
        <div className="prompt-card__actions">
          {onLink ? (
            <button type="button" className="prompt-card__action" onClick={handleLink}>
              Link
            </button>
          ) : null}
          <button type="button" className="prompt-card__action" onClick={handleOpen}>
            Open
          </button>
          <button
            type="button"
            className="prompt-card__action prompt-card__action--primary"
            onClick={handleOpen}
          >
            Use Prompt
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromptCard;
